import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { PROJECT_FILES_QUERY } from '../../graphql/queries';
import { formatDuration } from '../../utils/video360';
import styles from './WalkthroughCards.module.css';
import Icon from '../../components/common/Icon';

const PANEL_VIDEOS = 3;

interface VideoFile {
  id: string;
  originalName: string;
  status: string;
  createdAt: string;
  thumbnailUrl?: string | null;
  videoDuration?: number | null;
}

interface WalkthroughCardsProps {
  projectId: string;
  matterportUrl?: string | null;
  matterportAllowed: boolean;
  canEditMatterport: boolean;
  videosAllowed: boolean;
  onOpenMatterport: () => void;
  onEditMatterport: () => void;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

// The overview panel's alternative to the map (project settings → Overview
// panel): the Matterport tour and the latest 360 videos as thumbnail tiles.
const WalkthroughCards = ({
  projectId, matterportUrl, matterportAllowed, canEditMatterport, videosAllowed, onOpenMatterport, onEditMatterport,
}: WalkthroughCardsProps) => {
  const navigate = useNavigate();
  const { data } = useQuery(PROJECT_FILES_QUERY, {
    variables: { projectId, type: 'video360', page: 1, limit: PANEL_VIDEOS },
    skip: !videosAllowed,
    fetchPolicy: 'cache-and-network',
  });
  const videos: VideoFile[] = data?.projectFiles?.files ?? [];
  const videoTotal: number = data?.projectFiles?.total ?? 0;
  const hasTour = matterportAllowed && !!matterportUrl;
  const videosPath = `/projects/${projectId}/video360`;

  const summary = [
    hasTour ? '1 Matterport' : null,
    videoTotal > 0 ? `${videoTotal} video${videoTotal === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <div className={styles.title}>
          <Icon name="vrpano" />
          <b>Walkthroughs</b>
          {summary && <small>{summary}</small>}
        </div>
        <div className={styles.headLinks}>
          {videosAllowed && videoTotal > PANEL_VIDEOS && (
            <button type="button" className={styles.link} onClick={() => navigate(videosPath)}>All</button>
          )}
          {videosAllowed && (
            <button type="button" className={styles.link} onClick={() => navigate(`${videosPath}?upload=1`)}>
              <Icon name="add" />
              Add
            </button>
          )}
        </div>
      </div>

      {!hasTour && videos.length === 0 ? (
        <div className={styles.empty}>
          <Icon name="vrpano" />
          <p>No walkthroughs yet</p>
          {matterportAllowed && canEditMatterport && (
            <button type="button" className={styles.emptyBtn} onClick={onEditMatterport}>Add Matterport tour</button>
          )}
          {videosAllowed && (
            <button type="button" className={styles.emptyBtn} onClick={() => navigate(`${videosPath}?upload=1`)}>Upload 360 video</button>
          )}
        </div>
      ) : (
        <div className={styles.grid}>
          {hasTour && (
            <button type="button" className={styles.tile} onClick={onOpenMatterport}>
              <span className={`${styles.thumb} ${styles.thumbTour}`}>
                <Icon name="view_in_ar" />
                <span className={styles.external}>External</span>
              </span>
              <b>Matterport tour</b>
              <small>matterport.com</small>
            </button>
          )}
          {videos.map((v) => (
            <button
              key={v.id}
              type="button"
              className={styles.tile}
              onClick={() => navigate(v.status === 'ready' ? `/projects/${projectId}/viewer-360/${v.id}` : videosPath)}
            >
              <span className={`${styles.thumb} ${v.status === 'ready' ? styles.thumbVideo : styles.thumbPending}`}>
                {v.thumbnailUrl
                  ? <img src={v.thumbnailUrl} alt="" />
                  : <Icon name={v.status === 'ready' ? 'play_arrow' : 'hourglass_top'} />}
              </span>
              <b title={v.originalName}>{v.originalName}</b>
              <small>
                {v.status === 'ready'
                  ? `${formatDate(v.createdAt)}${v.videoDuration != null ? ` · ${formatDuration(v.videoDuration)}` : ''}`
                  : v.status}
              </small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default WalkthroughCards;
